<template>
  <div ref="rootEl" class="px-2 py-3 select-none">
    <!-- Month navigation header -->
    <div class="flex items-center justify-between mb-2 px-1">
      <button
        @click="prevMonth"
        class="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Previous month"
      >
        <svg class="w-3 h-3 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>

      <span class="text-xs font-semibold text-gray-700 dark:text-gray-300">{{ headerLabel }}</span>

      <button
        @click="nextMonth"
        class="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Next month"
      >
        <svg class="w-3 h-3 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>

    <!-- Day-of-week headers -->
    <div class="grid mb-1" :class="showWorkWeekOnly ? 'grid-cols-5' : 'grid-cols-7'">
      <span
        v-for="d in dayHeaders"
        :key="d"
        class="text-center text-[10px] font-medium text-gray-400 dark:text-gray-500 py-0.5"
      >{{ d }}</span>
    </div>

    <!-- Day cells -->
    <div class="grid" :class="showWorkWeekOnly ? 'grid-cols-5' : 'grid-cols-7'">
      <button
        v-for="day in calendarDays"
        :key="day.key"
        @click="day.currentMonth && emit('select', day.date)"
        :disabled="!day.currentMonth"
        :aria-label="day.date.toDateString()"
        :class="[
          'w-7 h-7 mx-auto flex items-center justify-center rounded-full text-[11px] transition-colors',
          day.isToday
            ? 'bg-blue-600 text-white font-bold'
            : day.isSelected
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold'
              : day.currentMonth
                ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                : 'text-gray-300 dark:text-gray-600 cursor-default',
        ]"
      >
        {{ day.day }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useGlobalStore }           from 'stores/global.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'

const globalStore   = useGlobalStore()
const settingsStore = useCalendarSettingsStore()
const showWorkWeekOnly = computed(() => settingsStore.show_only_work_week)
const rootEl      = ref(null)

const MINI_THRESHOLD = 20
const MINI_COOLDOWN  = 350
let _miniLastNavAt   = 0

function onMiniWheel(e) {
  const now   = Date.now()
  const absDy = Math.abs(e.deltaY)
  const absDx = Math.abs(e.deltaX)
  if (absDy < absDx || absDy < MINI_THRESHOLD) return
  e.preventDefault()
  if (now - _miniLastNavAt < MINI_COOLDOWN) return
  _miniLastNavAt = now
  if (e.deltaY > 0) nextMonth(); else prevMonth()
}

onMounted(()  => rootEl.value?.addEventListener('wheel', onMiniWheel, { passive: false }))
onUnmounted(() => rootEl.value?.removeEventListener('wheel', onMiniWheel))

const props = defineProps({
  selectedDate: { type: Date, default: null },
})

const emit = defineEmits(['select'])

const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const dayHeaders = computed(() => {
  if (showWorkWeekOnly.value) return ['M', 'T', 'W', 'T', 'F']
  const start = Number(globalStore.user?.start_day_of_week ?? 0)
  return [...DAY_INITIALS.slice(start), ...DAY_INITIALS.slice(0, start)]
})

// Internal display month — starts at the selected date's month or today
const display = ref(new Date(props.selectedDate ?? new Date()))
display.value.setDate(1)

// Follow the main calendar when it navigates to a different month
watch(() => props.selectedDate, (newDate) => {
  if (!newDate) return
  const d = new Date(newDate); d.setDate(1)
  if (d.getFullYear() !== display.value.getFullYear() || d.getMonth() !== display.value.getMonth()) {
    display.value = d
  }
})

function prevMonth() {
  const d = new Date(display.value)
  d.setMonth(d.getMonth() - 1)
  display.value = d
}

function nextMonth() {
  const d = new Date(display.value)
  d.setMonth(d.getMonth() + 1)
  display.value = d
}

const headerLabel = computed(() =>
  display.value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
)

const calendarDays = computed(() => {
  const today    = new Date(); today.setHours(0,0,0,0)
  const selected = props.selectedDate ? new Date(props.selectedDate) : null
  if (selected) selected.setHours(0,0,0,0)

  const year     = display.value.getFullYear()
  const month    = display.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay  = new Date(year, month + 1, 0)
  const workWeek = showWorkWeekOnly.value
  const isWeekend = d => d.getDay() === 0 || d.getDay() === 6
  const cols     = workWeek ? 5 : 7

  // In work-week mode always anchor to Monday; otherwise use the user's start_day_of_week
  const startDay = workWeek ? 1 : Number(globalStore.user?.start_day_of_week ?? 0)
  const offset   = (firstDay.getDay() - startDay + 7) % 7

  const days = []

  // Pad start with previous month's days
  for (let i = 0; i < offset; i++) {
    const d = new Date(year, month, 1 - offset + i)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, day: d.getDate(), currentMonth: false, isToday: false, isSelected: false, key: `prev-${i}` })
  }

  // Current month days
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const d = new Date(year, month, i); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({
      date:         d,
      day:          i,
      currentMonth: true,
      isToday:      d.getTime() === today.getTime(),
      isSelected:   selected ? d.getTime() === selected.getTime() : false,
      key:          `cur-${i}`,
    })
  }

  // Pad end to complete the last row
  let nextDay = 1
  while (days.length % cols !== 0) {
    const d = new Date(year, month + 1, nextDay++)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, day: d.getDate(), currentMonth: false, isToday: false, isSelected: false, key: `next-${nextDay}` })
  }

  return days
})
</script>
